import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Chip,
  Button,
  ButtonBase,
  Checkbox,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  ArrowBack,
  CheckCircle,
  CloudUpload,
  ExpandLess,
  ExpandMore,
  InsertDriveFile,
  Link as LinkIcon,
  Visibility,
  VisibilityOff,
  WarningRounded,
} from "@material-ui/icons";
import { Link as RouterLink } from "react-router-dom";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import { listTypebots, importTypebots, importFlowsFile } from "../../services/flowBuilders.api";
import { findAllQueues } from "../../services/queue.api";
import { listPrompts } from "../../services/prompt.api";
import { FLOW_CHANNELS, FLOW_CHANNEL_META } from "../../constants/flowChannels";

/**
 * Importar (0434 I2) — asistente de 3 pasos: ¿de dónde? → ¿qué? → revisar.
 *
 * Antes era una sola pantalla con dos pestañas técnicas y seis campos a la vista, con textos
 * cortados y botones grises que no decían qué faltaba. Ahora:
 *   - una pregunta por paso;
 *   - el departamento solo se pide si los bots transfieren a una persona, y el agente IA solo si
 *     tienen bloques de IA (lo dice el backend en la vista previa: `necesita`);
 *   - lo demás va en «Opciones avanzadas», plegado;
 *   - el botón de importar dice qué falta.
 * El motor del importador (0422) no cambia: lo que se enseña en la revisión es lo que se crea.
 */
const useStyles = makeStyles((theme) => ({
  pasos: { display: "flex", gap: 16, marginBottom: 20, color: theme.palette.text.secondary, fontSize: 13 },
  pasoActivo: { color: theme.palette.text.primary, fontWeight: 600 },
  tarjetas: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" } },
  tarjeta: {
    display: "flex", flexDirection: "column", alignItems: "flex-start", textAlign: "left", gap: 6,
    width: "100%", padding: 20, borderRadius: 10, border: `1px solid ${theme.palette.divider}`,
    transition: "border-color .15s, background .15s",
    "&:hover": { borderColor: theme.palette.primary.main, background: theme.palette.action.hover },
  },
  tarjetaSel: { borderColor: theme.palette.primary.main, background: theme.palette.action.selected },
  tarjetaOff: { opacity: 0.5, cursor: "default", "&:hover": { borderColor: theme.palette.divider, background: "none" } },
  icono: { fontSize: 30, color: theme.palette.primary.main },
  ayuda: { color: theme.palette.text.secondary, fontSize: 13, lineHeight: 1.45 },
  lista: { maxHeight: 260, overflow: "auto", border: `1px solid ${theme.palette.divider}`, borderRadius: 8, padding: "4px 8px" },
  soltar: {
    border: `2px dashed ${theme.palette.divider}`, borderRadius: 10, padding: 28, textAlign: "center", cursor: "pointer",
    "&:hover": { borderColor: theme.palette.primary.main },
  },
  resumen: { padding: 16, borderRadius: 10, background: theme.palette.action.hover, marginBottom: 16 },
  pregunta: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap", margin: "10px 0" },
  falta: { color: theme.palette.warning.main, fontSize: 13, marginRight: "auto", paddingLeft: 8 },
}));

const ACCION = {
  crear: "nuevo",
  actualizar: "se actualiza",
  "sin cambios": "sin cambios",
  editado: "editado aquí · se respeta",
  sobrescribir: "editado aquí · se reemplaza",
};
const SE_ESCRIBE = ["crear", "actualizar", "sobrescribir"];

/** Quita del texto técnico lo que no aporta al usuario («Group #15», prefijos internos). */
const legible = (texto) =>
  String(texto || "")
    .replace(/\s*·?\s*Group #\d+/g, "")
    .replace(/\(pendiente R\d\)/g, "")
    .trim();

const ImportTypebotModal = ({ open, onClose, onImported, channel }) => {
  const classes = useStyles();
  // Canal de los flujos que se crean desde Typebot (0438 Parte VII). Desde «Nuevo flujo» llega elegido;
  // desde la lista se elige aquí. Un archivo de FlowBuilder trae ya el canal de cada flujo.
  const [canal, setCanal] = useState(channel || "baileys");
  useEffect(() => {
    if (open) setCanal(channel || "baileys");
  }, [open, channel]);
  const [paso, setPaso] = useState(1); // 1 origen · 2 qué · 3 revisar · 4 hecho
  const [origen, setOrigen] = useState("typebot"); // "typebot" | "flowbuilder" (0434 I2)
  const [paquete, setPaquete] = useState(null); // { file, datos } del archivo de FlowBuilder
  const [elegidosDep, setElegidosDep] = useState({}); // ref → id, lo que no se encontró por nombre
  const [elegidosAg, setElegidosAg] = useState({});
  const inputPaquete = useRef(null);
  const [modo, setModo] = useState(null); // "api" | "archivo"
  const [baseUrl, setBaseUrl] = useState("");
  const [token, setToken] = useState("");
  const [verToken, setVerToken] = useState(false);
  const [disponibles, setDisponibles] = useState(null);
  const [filtro, setFiltro] = useState("");
  const [elegidos, setElegidos] = useState([]);
  const [archivo, setArchivo] = useState(null); // { nombre, typebots[] }
  const [pegar, setPegar] = useState(false);
  const [jsonTexto, setJsonTexto] = useState("");
  const [queues, setQueues] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [queueId, setQueueId] = useState("");
  const [promptId, setPromptId] = useState("");
  const [prefijo, setPrefijo] = useState("Typebot · ");
  const [enlazados, setEnlazados] = useState(true);
  const [sobrescribir, setSobrescribir] = useState(false);
  const [avanzadas, setAvanzadas] = useState(false);
  const [detalle, setDetalle] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [previa, setPrevia] = useState(null);
  const [resultado, setResultado] = useState(null);
  const inputArchivo = useRef(null);

  useEffect(() => {
    if (!open) return;
    findAllQueues().then(({ data }) => setQueues(Array.isArray(data) ? data : data?.queues || [])).catch(() => setQueues([]));
    listPrompts().then(({ data }) => setPrompts(Array.isArray(data?.prompts) ? data.prompts : [])).catch(() => setPrompts([]));
  }, [open]);

  const reiniciar = () => {
    setPaso(1); setModo(null); setToken(""); setDisponibles(null); setElegidos([]); setFiltro("");
    setArchivo(null); setPegar(false); setJsonTexto(""); setPrevia(null); setResultado(null);
    setDetalle(false); setAvanzadas(false);
    setOrigen("typebot"); setPaquete(null); setElegidosDep({}); setElegidosAg({}); setSobrescribir(false);
    setCanal(channel || "baileys");
  };
  const cerrar = () => { reiniciar(); onClose(); };

  // ── Paso 2 · conectar o cargar ───────────────────────────────────────────────
  const conectar = async () => {
    setCargando(true);
    try {
      const { data } = await listTypebots({ baseUrl: baseUrl.trim(), token: token.trim() });
      setDisponibles(data.typebots || []);
      setElegidos([]);
    } catch (err) {
      toastError(err);
    } finally {
      setCargando(false);
    }
  };

  const leerTexto = (texto, nombre) => {
    try {
      const v = JSON.parse(texto);
      const lista = (Array.isArray(v) ? v : [v]).map((x) => x?.typebot || x);
      if (!lista.length || lista.some((t) => !t?.id || !Array.isArray(t?.groups))) throw new Error("no es un typebot");
      setArchivo({ nombre, typebots: lista });
    } catch {
      toast.error("Ese archivo no es un bot exportado de Typebot. En Typebot: menú del bot → Exportar.");
    }
  };
  const cargarArchivos = async (files) => {
    const lista = Array.from(files || []);
    if (!lista.length) return;
    const textos = await Promise.all(lista.map((f) => f.text()));
    const bots = [];
    for (const t of textos) {
      try { const v = JSON.parse(t); bots.push(...(Array.isArray(v) ? v : [v])); } catch { /* lo valida leerTexto */ }
    }
    leerTexto(JSON.stringify(bots), lista.map((f) => f.name).join(", "));
  };

  const cargarPaquete = async (files) => {
    const file = Array.from(files || [])[0];
    if (!file) return;
    try {
      const datos = JSON.parse(await file.text());
      if (datos?.formato !== "watoolx-flowbuilder" || !Array.isArray(datos.flujos)) throw new Error("formato");
      setPaquete({ file, datos });
    } catch {
      toast.error("Ese archivo no es una exportación de FlowBuilder. Se descarga desde Flujos → marcar flujos → Exportar.");
    }
  };

  const listaFiltrada = useMemo(
    () => (disponibles || []).filter((t) => !filtro || `${t.name} ${t.workspace}`.toLowerCase().includes(filtro.toLowerCase())),
    [disponibles, filtro]
  );
  const puedeSeguir = origen === "flowbuilder" ? !!paquete : modo === "api" ? elegidos.length > 0 : !!archivo;

  // ── Paso 3 · vista previa (se repite al cambiar una respuesta) ───────────────
  const payload = (crear) => ({
    opciones: {
      queueIdPorDefecto: queueId ? Number(queueId) : null,
      promptPlantillaId: promptId ? Number(promptId) : null,
      prefijoNombre: prefijo,
      incluirEnlazados: enlazados,
      sobrescribirEditados: sobrescribir,
    },
    crear,
    provider: canal,
    ...(modo === "api" ? { fuente: { baseUrl: baseUrl.trim(), token: token.trim() }, typebotIds: elegidos } : { typebots: archivo?.typebots || [] }),
  });

  const opcionesPaquete = () => ({
    departamentos: elegidosDep, agentesIA: elegidosAg,
    prefijoNombre: prefijo, sobrescribirEditados: sobrescribir,
  });

  const verPrevia = async () => {
    setCargando(true);
    try {
      if (origen === "flowbuilder") {
        const { data } = await importFlowsFile(paquete.file, false, opcionesPaquete());
        // Mismo formato que el de Typebot para reutilizar la pantalla de revisión.
        // Las preguntas que ya se mostraron se mantienen aunque el usuario las conteste (si no, el
        // selector desaparecería al elegir, porque el backend ya no lo da por pendiente).
        const unir = (antes = [], ahora = []) => [...antes, ...ahora.filter((x) => !antes.some((a) => a.ref === x.ref))];
        setPrevia((ant) => ({
          bots: data.flujos.map((x) => ({ typebotId: x.ref, nombre: x.nombre, accion: x.accion, flowId: x.flowId })),
          informe: data.avisos.map((a) => ({ nivel: "aviso", typebot: a.flujo, mensaje: a.mensaje })),
          pendientes: {
            departamentos: unir(ant?.pendientes?.departamentos, data.pendientes.departamentos),
            agentesIA: unir(ant?.pendientes?.agentesIA, data.pendientes.agentesIA),
          },
          necesita: { departamento: false, agenteIA: false },
        }));
        return;
      }
      const { data } = await importTypebots(payload(false));
      setPrevia(data);
    } catch (err) {
      toastError(err);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    if (paso === 3) verPrevia();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paso, queueId, promptId, enlazados, sobrescribir, elegidosDep, elegidosAg]);

  const importar = async () => {
    setCargando(true);
    try {
      if (origen === "flowbuilder") {
        const { data } = await importFlowsFile(paquete.file, true, opcionesPaquete());
        setResultado({ bots: data.flujos.map((x) => ({ typebotId: x.ref, nombre: x.nombre, accion: x.accion, flowId: x.flowId })) });
        setPaso(4);
        onImported && onImported();
        return;
      }
      const { data } = await importTypebots(payload(true));
      setResultado(data);
      setPaso(4);
      onImported && onImported();
    } catch (err) {
      toastError(err);
    } finally {
      setCargando(false);
    }
  };

  const nuevos = previa?.bots.filter((b) => b.accion === "crear").length || 0;
  const actualizados = previa?.bots.filter((b) => ["actualizar", "sobrescribir"].includes(b.accion)).length || 0;
  const aEscribir = nuevos + actualizados;
  const revisar = (previa?.informe || []).filter((x) => x.nivel !== "info" && !/elige un Agente IA plantilla/i.test(x.mensaje));
  const errores = (previa?.informe || []).filter((x) => x.nivel === "error");
  const pendDep = previa?.pendientes?.departamentos || [];
  const pendAg = previa?.pendientes?.agentesIA || [];
  const faltaDepto = (previa?.necesita?.departamento && !queueId) || pendDep.some((d) => !elegidosDep[d.ref]);
  const faltaAgente = (previa?.necesita?.agenteIA && !promptId) || pendAg.some((d) => !elegidosAg[d.ref]);
  const motivo = !previa
    ? "Preparando la revisión…"
    : errores.length
    ? "Hay problemas que impiden importar (ver detalle)"
    : faltaDepto
    ? "Elige el departamento para poder importar"
    : faltaAgente
    ? "Elige el agente IA para poder importar"
    : aEscribir === 0
    ? "No hay nada nuevo que importar"
    : "";

  const porBot = useMemo(() => {
    const g = {};
    for (const x of revisar) (g[x.typebot] = g[x.typebot] || []).push(x);
    return g;
  }, [revisar]);

  // ── Render ───────────────────────────────────────────────────────────────────
  const Pasos = () => (
    <div className={classes.pasos}>
      {["De dónde", "Qué importar", "Revisar"].map((t, i) => (
        <span key={t} className={paso === i + 1 ? classes.pasoActivo : undefined}>{i + 1}. {t}</span>
      ))}
    </div>
  );

  return (
    <Dialog open={open} onClose={cargando ? undefined : cerrar} maxWidth="sm" fullWidth>
      <DialogTitle>Importar flujos</DialogTitle>
      <DialogContent dividers>
        {paso < 4 && <Pasos />}

        {paso === 1 && (
          <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8, marginBottom: 16 }}>
            <span className={classes.ayuda}>Los flujos de Typebot se crean en el canal:</span>
            {FLOW_CHANNELS.map((c) => (
              <Chip
                key={c}
                size="small"
                clickable={!channel}
                onClick={channel ? undefined : () => setCanal(c)}
                avatar={<img src={FLOW_CHANNEL_META[c].icon} alt="" style={{ width: 16, height: 16 }} />}
                label={FLOW_CHANNEL_META[c].label}
                variant={canal === c ? "default" : "outlined"}
                color={canal === c ? "primary" : "default"}
                style={channel && canal !== c ? { opacity: 0.4 } : undefined}
              />
            ))}
          </Box>
        )}
        {paso === 1 && (
          <div className={classes.tarjetas}>
            <ButtonBase className={classes.tarjeta} onClick={() => { setOrigen("typebot"); setModo(null); setPaso(2); }}>
              <span className={classes.icono}>🤖</span>
              <Typography variant="subtitle1"><b>Desde Typebot</b></Typography>
              <span className={classes.ayuda}>Trae tus bots de Typebot y los convierte en flujos, listos para editar aquí.</span>
            </ButtonBase>
            <ButtonBase className={classes.tarjeta} onClick={() => { setOrigen("flowbuilder"); setModo("archivo"); setPrefijo(""); setPaso(2); }}>
              <InsertDriveFile className={classes.icono} />
              <Typography variant="subtitle1"><b>Archivo de FlowBuilder</b></Typography>
              <span className={classes.ayuda}>Flujos exportados desde esta u otra instalación (botón Exportar de la lista).</span>
            </ButtonBase>
          </div>
        )}

        {paso === 2 && origen === "typebot" && !modo && (
          <>
            <Typography variant="body2" gutterBottom>¿Cómo quieres traer tus bots?</Typography>
            <div className={classes.tarjetas}>
              <ButtonBase className={classes.tarjeta} onClick={() => setModo("api")}>
                <LinkIcon className={classes.icono} />
                <Typography variant="subtitle1"><b>Conectar con mi Typebot</b></Typography>
                <span className={classes.ayuda}>Con la dirección de tu Typebot y una clave de acceso. Eliges los bots de una lista.</span>
              </ButtonBase>
              <ButtonBase className={classes.tarjeta} onClick={() => setModo("archivo")}>
                <CloudUpload className={classes.icono} />
                <Typography variant="subtitle1"><b>Subir un archivo</b></Typography>
                <span className={classes.ayuda}>Si exportaste el bot desde Typebot (menú del bot → Exportar).</span>
              </ButtonBase>
            </div>
          </>
        )}

        {paso === 2 && origen === "typebot" && modo === "api" && (
          <Box display="flex" flexDirection="column" style={{ gap: 14 }}>
            <TextField
              label="Dirección de tu Typebot" placeholder="https://typebot.tuempresa.com"
              value={baseUrl} onChange={(e) => { setBaseUrl(e.target.value); setDisponibles(null); }}
              variant="outlined" size="small" fullWidth
              helperText="La que abres en el navegador para editar tus bots."
            />
            <TextField
              label="Clave de acceso" value={token} onChange={(e) => { setToken(e.target.value); setDisponibles(null); }}
              type={verToken ? "text" : "password"} autoComplete="off" variant="outlined" size="small" fullWidth
              helperText="En Typebot: tu perfil → Configuración → Claves API → Crear. No se guarda en ningún sitio."
              InputProps={{ endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setVerToken((v) => !v)}>{verToken ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}</IconButton>
                </InputAdornment>
              ) }}
            />
            {disponibles === null ? (
              <Box>
                <Button variant="contained" color="primary" onClick={conectar} disabled={cargando || !baseUrl.trim() || !token.trim()}>
                  {cargando ? <CircularProgress size={18} /> : "Conectar"}
                </Button>
              </Box>
            ) : disponibles.length === 0 ? (
              <Typography className={classes.ayuda}>Conectado, pero esa cuenta no tiene bots.</Typography>
            ) : (
              <>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="body2">Elige los bots ({elegidos.length} de {disponibles.length})</Typography>
                  <Link component="button" variant="body2" onClick={() => setElegidos(elegidos.length === listaFiltrada.length ? [] : listaFiltrada.map((t) => t.id))}>
                    {elegidos.length === listaFiltrada.length ? "Quitar todos" : "Elegir todos"}
                  </Link>
                </Box>
                {disponibles.length > 6 && (
                  <TextField placeholder="Buscar bot…" size="small" variant="outlined" value={filtro} onChange={(e) => setFiltro(e.target.value)} />
                )}
                <div className={classes.lista}>
                  {listaFiltrada.map((t) => (
                    <FormControlLabel
                      key={t.id} style={{ display: "flex", margin: 0 }}
                      control={<Checkbox size="small" color="primary" checked={elegidos.includes(t.id)}
                        onChange={(e) => setElegidos((p) => (e.target.checked ? [...p, t.id] : p.filter((x) => x !== t.id)))} />}
                      label={<span>{t.name} <span className={classes.ayuda}>· {t.workspace}</span></span>}
                    />
                  ))}
                </div>
                <span className={classes.ayuda}>Los bots a los que saltan los elegidos se traen solos.</span>
              </>
            )}
          </Box>
        )}

        {paso === 2 && origen === "flowbuilder" && (
          <>
            <input ref={inputPaquete} hidden type="file" accept=".json,application/json" onChange={(e) => cargarPaquete(e.target.files)} />
            <div
              className={classes.soltar}
              onClick={() => inputPaquete.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); cargarPaquete(e.dataTransfer.files); }}
            >
              {paquete ? (
                <>
                  <CheckCircle style={{ color: "#43a047" }} />
                  <Typography variant="body2">
                    <b>{paquete.datos.flujos.length} flujo{paquete.datos.flujos.length === 1 ? "" : "s"}</b>
                    {paquete.datos.archivos?.length ? ` y ${paquete.datos.archivos.length} archivo(s)` : ""} en {paquete.file.name}
                  </Typography>
                  <span className={classes.ayuda}>{paquete.datos.flujos.map((x) => x.nombre).join(" · ")}</span>
                </>
              ) : (
                <>
                  <CloudUpload className={classes.icono} />
                  <Typography variant="body2">Arrastra aquí el archivo <b>.json</b> exportado o haz clic para elegirlo</Typography>
                  <span className={classes.ayuda}>Imágenes, saltos entre flujos, departamentos y agentes se reenganchan solos.</span>
                </>
              )}
            </div>
          </>
        )}

        {paso === 2 && origen === "typebot" && modo === "archivo" && (
          <>
            <input ref={inputArchivo} hidden type="file" accept=".json,application/json" multiple onChange={(e) => cargarArchivos(e.target.files)} />
            <div
              className={classes.soltar}
              onClick={() => inputArchivo.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); cargarArchivos(e.dataTransfer.files); }}
            >
              {archivo ? (
                <>
                  <CheckCircle style={{ color: "#43a047" }} />
                  <Typography variant="body2"><b>{archivo.typebots.length} bot(s)</b> en {archivo.nombre}</Typography>
                  <span className={classes.ayuda}>{archivo.typebots.map((t) => t.name).join(" · ")}</span>
                </>
              ) : (
                <>
                  <CloudUpload className={classes.icono} />
                  <Typography variant="body2">Arrastra aquí el archivo <b>.json</b> o haz clic para elegirlo</Typography>
                  <span className={classes.ayuda}>Puedes elegir varios a la vez: así los saltos entre bots se conectan solos.</span>
                </>
              )}
            </div>
            <Box mt={1}>
              <Link component="button" variant="body2" onClick={() => setPegar((v) => !v)}>
                {pegar ? "Ocultar" : "¿No tienes el archivo? Pega el contenido"}
              </Link>
              <Collapse in={pegar}>
                <TextField
                  fullWidth multiline rows={5} margin="dense" variant="outlined" placeholder="Pega aquí el JSON exportado…"
                  value={jsonTexto} onChange={(e) => setJsonTexto(e.target.value)}
                  onBlur={() => jsonTexto.trim() && leerTexto(jsonTexto, "el texto pegado")}
                />
              </Collapse>
            </Box>
          </>
        )}

        {paso === 3 && (
          <>
            {!previa ? (
              <Box display="flex" alignItems="center" style={{ gap: 10 }}><CircularProgress size={20} /> Revisando…</Box>
            ) : (
              <>
                <div className={classes.resumen}>
                  <Typography variant="subtitle1">
                    {aEscribir === 0 ? "Todo está ya importado y al día." : (
                      <>
                        {nuevos > 0 && <>Se crearán <b>{nuevos}</b> flujo{nuevos === 1 ? "" : "s"}</>}
                        {nuevos > 0 && actualizados > 0 && " · "}
                        {actualizados > 0 && <><b>{actualizados}</b> se actualiza{actualizados === 1 ? "" : "n"}</>}
                      </>
                    )}
                  </Typography>
                  <span className={classes.ayuda}>{previa.bots.map((b) => `${b.nombre.trim()} (${ACCION[b.accion] || b.accion})`).join(" · ")}</span>
                </div>

                {previa.necesita?.departamento && (
                  <div className={classes.pregunta}>
                    <Typography variant="body2">Tus bots pasan la conversación a una persona.<br /><span className={classes.ayuda}>¿A qué departamento?</span></Typography>
                    <TextField select size="small" variant="outlined" value={queueId} onChange={(e) => setQueueId(e.target.value)} style={{ minWidth: 220 }} SelectProps={{ displayEmpty: true }}>
                      <MenuItem value="" disabled>Elegir departamento</MenuItem>
                      {queues.map((q) => <MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>)}
                    </TextField>
                  </div>
                )}
                {previa.necesita?.agenteIA && (
                  <div className={classes.pregunta}>
                    <Typography variant="body2">Tus bots usan inteligencia artificial.<br /><span className={classes.ayuda}>¿Qué agente IA responde? (se copia su proveedor y clave)</span></Typography>
                    <TextField select size="small" variant="outlined" value={promptId} onChange={(e) => setPromptId(e.target.value)} style={{ minWidth: 220 }} SelectProps={{ displayEmpty: true }}>
                      <MenuItem value="" disabled>Elegir agente IA</MenuItem>
                      {prompts.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                    </TextField>
                  </div>
                )}

                {pendDep.map((d) => (
                  <div key={d.ref} className={classes.pregunta}>
                    <Typography variant="body2">Usan el departamento <b>«{d.nombre}»</b>, que aquí no existe.<br /><span className={classes.ayuda}>¿Cuál usar en su lugar?</span></Typography>
                    <TextField select size="small" variant="outlined" value={elegidosDep[d.ref] || ""} onChange={(e) => setElegidosDep((p) => ({ ...p, [d.ref]: e.target.value }))} style={{ minWidth: 220 }} SelectProps={{ displayEmpty: true }}>
                      <MenuItem value="" disabled>Elegir departamento</MenuItem>
                      {queues.map((q) => <MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>)}
                    </TextField>
                  </div>
                ))}
                {pendAg.map((d) => (
                  <div key={d.ref} className={classes.pregunta}>
                    <Typography variant="body2">Usan el agente IA <b>«{d.nombre}»</b>, que aquí no existe.<br /><span className={classes.ayuda}>¿Cuál responde en su lugar?</span></Typography>
                    <TextField select size="small" variant="outlined" value={elegidosAg[d.ref] || ""} onChange={(e) => setElegidosAg((p) => ({ ...p, [d.ref]: e.target.value }))} style={{ minWidth: 220 }} SelectProps={{ displayEmpty: true }}>
                      <MenuItem value="" disabled>Elegir agente IA</MenuItem>
                      {prompts.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                    </TextField>
                  </div>
                ))}

                {revisar.length > 0 && (
                  <Box mt={1}>
                    <Link component="button" variant="body2" onClick={() => setDetalle((v) => !v)}>
                      <WarningRounded style={{ fontSize: 16, verticalAlign: "text-bottom", color: "#fb8c00" }} /> {revisar.length} cosa{revisar.length === 1 ? "" : "s"} que revisar después {detalle ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    </Link>
                    <Collapse in={detalle}>
                      <Box mt={1}>
                        {Object.entries(porBot).map(([bot, xs]) => (
                          <Box key={bot} mb={1}>
                            <Typography variant="body2"><b>{bot}</b></Typography>
                            {xs.map((x, i) => (
                              <Typography key={i} variant="body2" className={classes.ayuda} style={{ color: x.nivel === "error" ? "#e53935" : undefined }}>
                                • {legible(x.mensaje)}
                              </Typography>
                            ))}
                          </Box>
                        ))}
                      </Box>
                    </Collapse>
                  </Box>
                )}

                <Box mt={2}>
                  <Link component="button" variant="body2" onClick={() => setAvanzadas((v) => !v)}>
                    Opciones avanzadas {avanzadas ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                  </Link>
                  <Collapse in={avanzadas}>
                    <Box mt={1} display="flex" flexDirection="column" style={{ gap: 6 }}>
                      <TextField label="Los nombres empiezan por" size="small" variant="outlined" value={prefijo} onChange={(e) => setPrefijo(e.target.value)} style={{ maxWidth: 280 }} />
                      {origen === "typebot" && (
                        <FormControlLabel control={<Checkbox size="small" color="primary" checked={enlazados} onChange={(e) => setEnlazados(e.target.checked)} />} label={<span className={classes.ayuda}>Traer también los bots a los que saltan</span>} />
                      )}
                      <FormControlLabel control={<Checkbox size="small" checked={sobrescribir} onChange={(e) => setSobrescribir(e.target.checked)} />} label={<span className={classes.ayuda}>Reemplazar los flujos que alguien editó aquí después de importarlos (se pierden esos cambios)</span>} />
                    </Box>
                  </Collapse>
                </Box>
              </>
            )}
          </>
        )}

        {paso === 4 && resultado && (
          <Box textAlign="center" py={2}>
            <CheckCircle style={{ fontSize: 44, color: "#43a047" }} />
            <Typography variant="h6">Listo: {resultado.bots.filter((b) => SE_ESCRIBE.includes(b.accion)).length} flujo(s) importados</Typography>
            <Box mt={2} textAlign="left">
              {resultado.bots.filter((b) => b.flowId).map((b) => (
                <Box key={b.typebotId} display="flex" justifyContent="space-between" py={0.5}>
                  <Typography variant="body2">{b.nombre.trim()} <span className={classes.ayuda}>· {ACCION[b.accion] || b.accion}</span></Typography>
                  <Link component={RouterLink} to={`/flowbuilders/${b.flowId}/edit`} onClick={cerrar}>Abrir</Link>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {paso > 1 && paso < 4 && (
          <Button startIcon={<ArrowBack />} disabled={cargando} onClick={() => {
            if (paso === 3) { setPaso(2); setPrevia(null); }
            else if (origen === "flowbuilder") { setPaquete(null); setOrigen("typebot"); setModo(null); setPaso(1); }
            else if (modo) { setModo(null); setDisponibles(null); setArchivo(null); }
            else setPaso(1);
          }}>Atrás</Button>
        )}
        {paso === 3 && motivo && <span className={classes.falta}>{motivo}</span>}
        <Button onClick={cerrar} disabled={cargando}>{paso === 4 ? "Cerrar" : "Cancelar"}</Button>
        {paso === 2 && modo && (
          <Button color="primary" variant="contained" disabled={!puedeSeguir || cargando} onClick={() => setPaso(3)}>Siguiente</Button>
        )}
        {paso === 3 && (
          <Button color="primary" variant="contained" disabled={cargando || !!motivo} onClick={importar}>
            {cargando && previa ? <CircularProgress size={18} /> : `Importar ${aEscribir || ""} flujo${aEscribir === 1 ? "" : "s"}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportTypebotModal;
